import { Button } from '@/components/ui/shadcn/button'
import { Input } from '@/components/ui/shadcn/input'
import { trpc } from '@/config/trpc'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../ui/shadcn/form'

const formSchema = z
  .object({
    password: z.string().min(1, { message: 'Password is required' }),
    confirmPassword: z.string().min(1, { message: 'Password confirmation is required' }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  })

export function ResetPasswordForm({ token }: { token: string }) {
  const navigate = useNavigate()

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  })

  const { mutateAsync: resetPassword, isPending } = trpc.auth.resetPassword.useMutation()

  const handleResetPassword = async (values: z.infer<typeof formSchema>) => {
    try {
      await resetPassword({ token, password: values.password })
      navigate('/login')

      toast.success('Password reset successfully!', {
        description: 'You can now log in with your new password.',
      })
    } catch (error) {
      form.setError('root', {
        message: error instanceof Error ? error.message : 'An error occurred',
      })
    }
  }

  return (
    <div>
      <p className="py-2">Please enter your new password below.</p>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleResetPassword)} className="space-y-4">
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Password</FormLabel>
                <FormControl>
                  <Input placeholder="Password" size="lg" type="password" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Confirm password</FormLabel>
                <FormControl>
                  <Input placeholder="Repeat password" size="lg" type="password" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {form.formState.errors.root && (
            <div className="mt-1">
              <FormMessage>{form.formState.errors.root.message}</FormMessage>
            </div>
          )}

          <Button type="submit" disabled={isPending} className="mt-4 w-full" size="lg">
            {isPending ? (
              <span className="border-primary/30 border-t-primary inline-block h-4 w-4 animate-spin rounded-full border-2" />
            ) : (
              'Reset password'
            )}
          </Button>
        </form>
      </Form>

      <p className="text-muted-foreground mt-2 text-center text-xs">
        Back to{' '}
        <a
          className="text-primary cursor-pointer underline"
          onClick={(e) => {
            e.preventDefault()
            navigate('/login')
          }}>
          Login
        </a>
        .
      </p>
    </div>
  )
}
